import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { listenTrialMaxWords, validTextLength } from '@/environment';
import { activePlayerIdAtom, PlayerMode, useAudioPlayer } from '@/hooks/useAudioPlayer';
import Loading from '@/images/Loading.svg';
import PlayPreview from '@/images/PlayPreview.svg';
import VolumePreview from '@/images/VolumePreview.svg';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { DEFAULT_VOICE_CONFIG, ttsGenerationErrors } from '@/state/presenters';
import { useAgentEditorStore } from '@/state/root';
import { getWordsFromText } from '@/utils/utils';
import { IVoice, PlanGroup, Providers, VoiceAccess } from '@d-id/types';
import styled from '@emotion/styled';
import { Badge } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

const HeaderButton = styled.button`
    border: none;
    height: 2rem;
    width: 2rem;
    box-sizing: border-box;
    padding: 0;
    color: inherit;

    :disabled {
        opacity: 0.5;
    }
`;
const ListenButton = styled(HeaderButton)`
    background-color: transparent;
    object-fit: contain;
    margin: 0 1.75rem;
    cursor: pointer;
    :disabled {
        cursor: default;
    }
`;

const ListenImage = styled.img`
    height: 2rem;
    width: 2rem;
`;

const PreviewImage = styled.img`
    height: 2rem;
    width: 2rem;
    padding: 3px;
    margin-top: 0.3rem;
`;

const ImageWrapper = styled.div<{ isPlaying: boolean }>`
    height: 2.5rem;
    width: 2.5rem;
    background-color: ${({ theme, isPlaying }) => theme.colors.black.main + (isPlaying ? 'ff' : '10')};
    border-radius: 1.25rem;
    margin-top: -0.3rem;
`;

const PremiumBadge = styled(Badge, { shouldForwardProp: p => p !== 'show' })<{ show: boolean }>`
    & .MuiBadge-badge {
        background: rgba(255, 184, 0, 0.1);
        display: ${({ show }) => (show ? 'inline' : 'none')};
        height: fit-content;
        padding: 0 2px;
        right: 28%;
    }
`;

interface Props {
    disabled?: boolean;
    onClick?: () => void;
    voice: IVoice;
    text: string;
    from?: string;
}

export const Listen = observer(({ disabled, onClick, voice, text, from = 'voices' }: Props) => {
    const agentEditorStore = useAgentEditorStore();

    const mixpanel = useAnalytics();

    const activePlayerId = useRecoilValue(activePlayerIdAtom);
    const voiceLanguage = voice.language.split(' ')[0].toLowerCase();
    const playerId = `${voice.id}-${voiceLanguage}`;

    const isSupportedProvider = voice?.provider !== Providers.Amazon;
    const isExternalPrivate = voice.access === VoiceAccess.ExternalPrivate;
    const isOtherVoicePlaying = !!activePlayerId && activePlayerId !== playerId;

    const voiceConfig = agentEditorStore.ttsVoiceConfig || DEFAULT_VOICE_CONFIG;

    const { user, isAnonymous } = useAuth();
    const isTrialOrAnonymous = isAnonymous || user?.planGroup === PlanGroup.TRIAL;

    const validLength = text?.length >= validTextLength;

    const listenDisabled = useMemo(
        () => !validLength || !voice || disabled || isExternalPrivate || !isSupportedProvider || isOtherVoicePlaying,
        [validLength, voice, disabled, isSupportedProvider, activePlayerId, playerId]
    );
    const allowedText = isTrialOrAnonymous ? getWordsFromText(text, listenTrialMaxWords) : text;

    const { playerMode, error, play, pause } = useAudioPlayer({
        voice,
        text: allowedText,
        voiceConfig,
        language: voice.language,
        playerId,
    });

    const onPlay = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();
        mixpanel?.track('tts-listen', {
            from,
            event: 'play',
            voice,
            style: voiceConfig.style,
            textLength: allowedText.length,
            originalLength: text?.length,
        });

        onClick?.();

        if (voice) {
            play({ useIndicators: true, volume: 1 });
        }
    };

    const onPause = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        pause?.();
    };

    const tooltipText = useMemo(() => {
        if (!isSupportedProvider) {
            return "Preview isn't available for this voice";
        }
        if (voice) {
            return !validLength ? 'Add script to play voice' : '';
        }
        return 'Listen';
    }, [isSupportedProvider, listenDisabled]);

    useEffect(() => {
        if (error) {
            const errorDetails = ttsGenerationErrors[error];
            agentEditorStore.setErrors(errors => ({ ...errors, ttsError: errorDetails }));
            mixpanel?.track('tts-listen', {
                event: 'error',
                error,
                textLength: allowedText.length,
                originalLength: text?.length,
                voice,
                style: voiceConfig.style,
            });
            if (error === 'unknown') {
                logger.error('Error playing TTS', { error: errorDetails, text, voice, style: voiceConfig.style });
            }
        }
    }, [error]);

    if (playerMode === PlayerMode.Loading && playerId === activePlayerId) {
        return (
            <CustomTooltip title="" placement="top" containerWidth="5rem" cursor="pointer" containerHeight="2rem">
                <ListenButton id="listen">
                    <ListenImage src={Loading} />
                </ListenButton>
            </CustomTooltip>
        );
    }

    if (playerMode === PlayerMode.Playing && playerId === activePlayerId) {
        return (
            <CustomTooltip
                title=""
                placement="top"
                containerWidth="5rem"
                containerHeight="2rem"
                disabled={!validLength || !voice || disabled}
                cursor="pointer"
                onClick={onPause}>
                <ListenButton id="listen" disabled={!validLength || !voice || disabled}>
                    <ImageWrapper isPlaying>
                        <PreviewImage src={VolumePreview} />
                    </ImageWrapper>
                </ListenButton>
            </CustomTooltip>
        );
    }

    return (
        <PremiumBadge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} show={isTrialOrAnonymous}>
            <CustomTooltip
                title={tooltipText}
                placement="top"
                containerWidth="5rem"
                containerHeight="2rem"
                onClick={onPlay}
                disabled={listenDisabled}
                cursor={'pointer'}>
                <ListenButton id="listen" aria-label="play preview" disabled={listenDisabled}>
                    <ImageWrapper isPlaying={false}>
                        <PreviewImage src={PlayPreview} />
                    </ImageWrapper>
                </ListenButton>
            </CustomTooltip>
        </PremiumBadge>
    );
});
